import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Bitcoin, Wallet, ArrowRight, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

const AdminPayments = () => {
  const [data, setData] = useState(null);
  const [site, setSite] = useState(null);
  const [siteSaving, setSiteSaving] = useState(false);

  useEffect(() => { api.getPayments().then(setData).catch(() => {}); api.getSite().then(setSite).catch(() => {}); }, []);

  if (!data || !site) return <p className="text-zinc-500">Loading…</p>;

  const setProvider = (id, patch) => setData({ ...data, providers: data.providers.map((p) => p.id === id ? { ...p, ...patch } : p) });
  const save = async () => { try { await api.putPayments(data); toast.success('Payments saved'); api.getPayments().then(setData); } catch { toast.error('Save failed'); } };

  const checkout = site.checkout || { payment_step_enabled: false, require_customer_login: true };
  const setCheckout = (patch) => setSite({ ...site, checkout: { ...checkout, ...patch } });
  const saveSite = async () => {
    setSiteSaving(true);
    try { await api.putSite(site); toast.success('Checkout flow saved'); api.getSite().then(setSite); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSiteSaving(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-zinc-950 border-[#d4af37]/30 p-6" data-testid="checkout-flow-card">
        <div className="flex items-start gap-3">
          <PhoneCall className="h-6 w-6 text-[#d4af37] shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="serif-italic text-2xl text-[#d4af37] mb-1">Checkout Flow</h3>
            <p className="text-zinc-400 text-sm mb-4">Control what happens after a customer places an order. With payment step disabled, orders route to a &ldquo;thank you, we&rsquo;ll contact you&rdquo; page — you receive an email with all details.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <label className="flex items-center justify-between border border-zinc-900 rounded p-3" data-testid="toggle-payment-step">
                <div>
                  <p className="text-zinc-100 text-sm">Enable payment step</p>
                  <p className="text-zinc-500 text-xs mt-0.5">Off = &ldquo;representative will contact you&rdquo; flow</p>
                </div>
                <Switch checked={!!checkout.payment_step_enabled} onCheckedChange={(v) => setCheckout({ payment_step_enabled: v })} />
              </label>
              <label className="flex items-center justify-between border border-zinc-900 rounded p-3" data-testid="toggle-require-login">
                <div>
                  <p className="text-zinc-100 text-sm">Require customer account</p>
                  <p className="text-zinc-500 text-xs mt-0.5">Customer must register (18+) before ordering</p>
                </div>
                <Switch checked={checkout.require_customer_login !== false} onCheckedChange={(v) => setCheckout({ require_customer_login: v })} />
              </label>
            </div>
            <Button onClick={saveSite} disabled={siteSaving} className="btn-gold mt-4" data-testid="save-checkout-flow">{siteSaving ? 'Saving…' : 'Save checkout flow'}</Button>
          </div>
        </div>
      </Card>

      <Card className="bg-zinc-950 border-zinc-900 p-6">
        <div className="flex flex-wrap gap-4 items-center text-zinc-300">
          <div className="flex items-center gap-2"><Wallet className="h-5 w-5 text-[#d4af37]" /> Customer pays in <span className="text-[#d4af37]">iDEAL / Card / Crypto</span></div>
          <ArrowRight className="h-4 w-4 text-zinc-500" />
          <div className="flex items-center gap-2"><Bitcoin className="h-5 w-5 text-[#d4af37]" /> You receive <span className="text-[#d4af37]">BTC / ETH / USDT</span> in your wallet</div>
        </div>
        <p className="text-zinc-500 text-sm mt-3">Toggle providers and paste API keys. <span className="text-emerald-400">Mollie is wired live</span> — paste your key + enable, then test from /checkout.</p>
      </Card>

      <Tabs defaultValue="providers">
        <TabsList className="bg-zinc-900">
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="wallets">Crypto wallets</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="providers" className="mt-6 grid md:grid-cols-2 gap-4">
          {data.providers.map((m) => (
            <Card key={m.id} className="bg-zinc-950 border-zinc-900 p-5">
              <div className="flex items-start justify-between gap-3">
                <div><h4 className="serif-italic text-[#d4af37] text-xl">{m.name}</h4><p className="text-zinc-400 text-sm mt-1">{m.desc}</p></div>
                <Switch checked={!!m.enabled} onCheckedChange={(v) => setProvider(m.id, { enabled: v })} />
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">{(m.accepts||[]).map((a) => <Badge key={a} variant="outline" className="border-zinc-800 text-zinc-300 text-xs">{a}</Badge>)}</div>
              <p className="mt-4 text-xs text-zinc-500">Settles in: <span className="text-zinc-300">{m.settles}</span></p>
              <Input value={m.api_key || ''} onChange={(e) => setProvider(m.id, { api_key: e.target.value })} placeholder={m.api_key_masked ? `Saved: ${m.api_key_masked}` : 'API key / store ID'} className="bg-transparent border-zinc-800 mt-3" type="password" />
              {m.id === 'mollie' && <p className="text-emerald-400 text-xs mt-2">✓ Live integration — starts with <code>live_</code> or <code>test_</code></p>}
            </Card>
          ))}
          <div className="md:col-span-2"><Button className="btn-gold" onClick={save}>Save providers</Button></div>
        </TabsContent>

        <TabsContent value="wallets" className="mt-6">
          <Card className="bg-zinc-950 border-zinc-900 p-6 space-y-4 max-w-2xl">
            <div><Label>BTC payout address</Label><Input value={data.btc_address || ''} onChange={(e) => setData({ ...data, btc_address: e.target.value })} placeholder="bc1q…" className="bg-transparent border-zinc-800 mt-1 font-mono text-xs" /></div>
            <div><Label>ETH / USDT (ERC-20) address</Label><Input value={data.eth_address || ''} onChange={(e) => setData({ ...data, eth_address: e.target.value })} placeholder="0x…" className="bg-transparent border-zinc-800 mt-1 font-mono text-xs" /></div>
            <div><Label>LTC address</Label><Input value={data.ltc_address || ''} onChange={(e) => setData({ ...data, ltc_address: e.target.value })} placeholder="ltc1…" className="bg-transparent border-zinc-800 mt-1 font-mono text-xs" data-testid="wallet-ltc" /></div>
            <div>
              <Label>USDT address</Label>
              <Input value={data.usdt_address || ''} onChange={(e) => setData({ ...data, usdt_address: e.target.value })} placeholder="TRC20 recommended (T…) or ERC20 (0x…)" className="bg-transparent border-zinc-800 mt-1 font-mono text-xs" data-testid="wallet-usdt" />
              <select value={data.usdt_network || 'TRC20'} onChange={(e) => setData({ ...data, usdt_network: e.target.value })} className="bg-transparent border border-zinc-800 mt-2 rounded px-2 h-9 text-sm text-zinc-100">
                <option value="TRC20">TRC20 (lowest fees)</option>
                <option value="ERC20">ERC20 (Ethereum)</option>
                <option value="BEP20">BEP20 (BNB Chain)</option>
              </select>
            </div>
            <div>
              <Label>Crypto direct discount (%)</Label>
              <Input type="number" step="0.1" value={data.crypto_discount_percent ?? 15} onChange={(e) => setData({ ...data, crypto_discount_percent: parseFloat(e.target.value) || 0 })} className="bg-transparent border-zinc-800 mt-1" data-testid="wallet-crypto-discount" />
              <p className="text-zinc-500 text-xs mt-1">Applied automatically when the customer picks Crypto (direct) at checkout.</p>
            </div>
            <div><Label>Auto-convert threshold (€)</Label><Input type="number" value={data.auto_convert_threshold} onChange={(e) => setData({ ...data, auto_convert_threshold: parseFloat(e.target.value) || 0 })} className="bg-transparent border-zinc-800 mt-1" /></div>
            <p className="text-zinc-500 text-xs">Tip: keep withdrawals over €50 to minimize on-chain fees — perfect for slowly building a long-term retirement BTC stack.</p>
            <Button className="btn-gold" onClick={save}>Save wallets</Button>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-6 grid md:grid-cols-2 gap-6">
          <Card className="bg-zinc-950 border-zinc-900 p-6 space-y-4">
            <h4 className="serif-italic text-[#d4af37] text-xl">Currency & display</h4>
            <div><Label>Storefront currency</Label><Input value={data.currency} onChange={(e) => setData({ ...data, currency: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
            <div className="flex items-center justify-between"><span className="text-zinc-300 text-sm">Show crypto price next to EUR</span><Switch checked={!!data.show_crypto_price} onCheckedChange={(v) => setData({ ...data, show_crypto_price: v })} /></div>
            <Button className="btn-gold" onClick={save}>Save</Button>
          </Card>
          <Card className="bg-zinc-950 border-zinc-900 p-6 space-y-4">
            <h4 className="serif-italic text-[#d4af37] text-xl">Compliance</h4>
            <p className="text-zinc-400 text-sm">KYC required only for orders ≥ €1,000 (configurable). Auto-attach age-verification (18+) for vape products.</p>
            <div className="flex items-center justify-between"><span className="text-zinc-300 text-sm">Age gate (18+)</span><Switch checked={!!data.age_gate} onCheckedChange={(v) => setData({ ...data, age_gate: v })} /></div>
            <div className="flex items-center justify-between"><span className="text-zinc-300 text-sm">KYC for high-value orders</span><Switch checked={!!data.kyc_high_value} onCheckedChange={(v) => setData({ ...data, kyc_high_value: v })} /></div>
            <Button className="btn-gold" onClick={save}>Save</Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPayments;
